// src/services/studentService.js
import { doc, updateDoc, addDoc, collection } from 'firebase/firestore';
import { db } from './firebase';
import { API_ENDPOINTS } from '../utils/constants';
import { query, where, getDocs } from 'firebase/firestore';

/**
 * Update student status and send notification
 */
export const updateStudentStatus = async (studentId, driverId, newStatus) => {
  const timestamp = new Date().toLocaleTimeString();
  
  try {
    // Update Firestore
    const studentRef = doc(db, 'students', studentId);
    const updateData = { status: newStatus };
    
    if (newStatus === 'picked-up') {
      updateData.pickupTime = timestamp;
    } else if (newStatus === 'delivered') {
      updateData.deliveryTime = timestamp;
    }
    
    await updateDoc(studentRef, updateData);
    
    // Call Cloud Function for email notification
    await fetch(API_ENDPOINTS.UPDATE_STUDENT_STATUS, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        studentId,
        status: newStatus,
        driverId
      })
    });
    
    return { success: true, timestamp };
  } catch (error) {
    console.error('Error updating student status:', error);
    throw new Error('Failed to update student status');
  }
};

/**
 * Add new student to database
 */
export const addStudent = async (studentData) => {
  try {
    const docRef = await addDoc(collection(db, 'students'), {
      ...studentData,
      status: 'pending',
      submissionDate: new Date(),
      archived: false,
      assignedDriver: studentData.needsPickup ? 'driver1' : null
    });
    
    return { success: true, id: docRef.id };
  } catch (error) {
    console.error('Error adding student:', error);
    throw new Error('Failed to add student');
  }
};

/**
 * Update student information
 */
export const updateStudent = async (studentId, updates) => {
  try {
    const studentRef = doc(db, 'students', studentId);
    await updateDoc(studentRef, updates);
    return { success: true };
  } catch (error) {
    console.error('Error updating student:', error);
    throw new Error('Failed to update student');
  }
};

/**
 * Archive student
 */
export const archiveStudent = async (studentId) => {
  try {
    const studentRef = doc(db, 'students', studentId);
    await updateDoc(studentRef, {
      archived: true,
      archivedDate: new Date().toISOString()
    });
    return { success: true };
  } catch (error) {
    console.error('Error archiving student:', error);
    throw new Error('Failed to archive student');
  }
};

/**
 * Unarchive student
 */
export const unarchiveStudent = async (studentId) => {
  try {
    const studentRef = doc(db, 'students', studentId);
    await updateDoc(studentRef, {
      archived: false,
      archivedDate: null
    });
    return { success: true };
  } catch (error) {
    console.error('Error unarchiving student:', error);
    throw new Error('Failed to unarchive student');
  }
};

/**
 * Update driver assignment
 */
export const updateDriverAssignment = async (studentId, driverId) => {
  try {
    const studentRef = doc(db, 'students', studentId);
    await updateDoc(studentRef, { assignedDriver: driverId });
    return { success: true };
  } catch (error) {
    console.error('Error updating driver assignment:', error);
    throw new Error('Failed to update driver assignment');
  }
};

/**
 * Add pending registration (before payment)
 * For BH students: updates existing record instead
 */
export const addPendingRegistration = async (registrationData) => {
  try {
    // Check if this is a BH student (billing type = third-party or bright-horizons)
    const isBHStudent = registrationData.billingType === 'third-party' || 
                        registrationData.billingType === 'bright-horizons' ||
                        registrationData.programSelection?.toLowerCase().includes('bright horizons');
    
if (isBHStudent) {
  // Find existing BH student by flexible matching (2 out of 3 fields)
  const studentsRef = collection(db, 'students');
  
  // Normalize search criteria
  const childFirstName = (registrationData.studentFirstName || registrationData.firstName || '').trim().toLowerCase();
  const childLastName = (registrationData.studentLastName || registrationData.lastName || '').trim().toLowerCase();
  const childDOB = registrationData.dateOfBirth;
  const parentEmail = (registrationData.parentEmail || '').trim().toLowerCase();
  
  console.log(`🔍 Looking for BH student: ${childFirstName} ${childLastName}, DOB: ${childDOB}, Email: ${parentEmail}`);
  
  // Query by billing type first (more efficient)
  const q = query(
    studentsRef,
    where('billingType', 'in', ['bright-horizons', 'third-party'])
  );
  
  const querySnapshot = await getDocs(q);
  
  // Manually filter with flexible matching (2 out of 3 criteria)
  const matchingDocs = [];
  
  querySnapshot.forEach(doc => {
    const student = doc.data();
    const studentFirstName = (student.firstName || '').trim().toLowerCase();
    const studentLastName = (student.lastName || '').trim().toLowerCase();
    const studentDOB = student.dateOfBirth;
    const studentEmail = (student.parentEmail || '').trim().toLowerCase();
    
    // Check matches for each criterion
    const nameMatch = (studentFirstName === childFirstName && studentLastName === childLastName);
    const dobMatch = (studentDOB === childDOB);
    const emailMatch = (studentEmail === parentEmail);
    
    // Count matches
    let matchCount = 0;
    if (nameMatch) matchCount++;
    if (dobMatch) matchCount++;
    if (emailMatch) matchCount++;
    
    // Match if 2 or more criteria match
    if (matchCount >= 2) {
      matchingDocs.push({
        doc: doc,
        matchCount: matchCount,
        nameMatch: nameMatch,
        dobMatch: dobMatch,
        emailMatch: emailMatch
      });
      console.log(`✅ Found match: ${doc.id} (${matchCount}/3 criteria matched - Name:${nameMatch} DOB:${dobMatch} Email:${emailMatch})`);
    }
  });
  
 if (matchingDocs.length > 0) {
  console.log(`✅ Found ${matchingDocs.length} matching record(s) - updating ALL with latest form data`);
  
  // Parse camp dates if this is a camp registration
  let newCareDates = [];
  if (registrationData.campStartDate && registrationData.campEndDate) {
    newCareDates = generateCareDates(
      registrationData.campStartDate, 
      registrationData.campEndDate
    );
    console.log(`📅 Generated ${newCareDates.length} care dates from camp registration`);
  }
  
  // Update ALL matching records
  const updatePromises = matchingDocs.map(async (match) => {
    const existingStudent = match.doc.data();
    
    // Merge care dates if student already has some
    let mergedCareDates = newCareDates;
    if (existingStudent.careDates && Array.isArray(existingStudent.careDates)) {
      // Combine existing and new dates, remove duplicates
      const allDates = [...existingStudent.careDates, ...newCareDates];
      const uniqueDates = allDates.filter((date, index, self) =>
        index === self.findIndex(d => d.date === date.date)
      );
      mergedCareDates = uniqueDates.sort((a, b) => a.date.localeCompare(b.date));
      console.log(`📅 Merged dates: ${existingStudent.careDates.length} existing + ${newCareDates.length} new = ${mergedCareDates.length} total`);
    }
    
    const updateData = {
      ...registrationData,
      careDates: mergedCareDates.length > 0 ? mergedCareDates : existingStudent.careDates,
      registrationComplete: true,
      formCompletedAt: registrationData.formSubmissionDate || new Date().toISOString(),
      lastFormUpdate: new Date().toISOString(),
      lastUpdated: new Date(),
      dateOfBirth: childDOB || registrationData.dateOfBirth
    };
    
    return updateDoc(match.doc.ref, updateData);
  });
  
  await Promise.all(updatePromises);
    
    console.log(`✅ Updated ${matchingDocs.length} BH student record(s) for: ${childFirstName} ${childLastName}`);
    return { 
      success: true, 
      updatedCount: matchingDocs.length,
      ids: matchingDocs.map(m => m.doc.id),
      action: 'updated',
      matchDetails: matchingDocs.map(m => ({
        id: m.doc.id,
        matched: `${m.matchCount}/3 criteria`
      }))
    };
} else {
  console.log(`⚠️ No existing BH record found for ${childFirstName} ${childLastName}, creating new student`);
  
  // Parse camp dates if this is a camp registration
  let careDates = [];
  if (registrationData.campStartDate && registrationData.campEndDate) {
    careDates = generateCareDates(
      registrationData.campStartDate, 
      registrationData.campEndDate
    );
  }
  
  // If no existing record, create new one in students collection
  const docRef = await addDoc(collection(db, 'students'), {
    ...registrationData,
    careDates: careDates.length > 0 ? careDates : undefined,
    registrationComplete: true,
    formCompletedAt: new Date().toISOString(),
    status: 'pending',
    archived: false,
    submissionDate: new Date()
  });
  return { success: true, id: docRef.id, action: 'created' };
}
    }
     else {
      // Regular student - save to pendingRegistrations for payment processing
      const docRef = await addDoc(collection(db, 'pendingRegistrations'), {
        ...registrationData,
        paymentStatus: 'pending',
        createdAt: new Date()
      });
      
      return { success: true, id: docRef.id };
    }
  } catch (error) {
    console.error('Error adding pending registration:', error);
    throw new Error('Failed to save registration');
  }
};

/**
 * Add third-party billing student (no payment required)
 */
export const addThirdPartyStudent = async (studentData) => {
  try {
    const docRef = await addDoc(collection(db, 'students'), {
      firstName: studentData.studentFirstName,
      lastName: studentData.studentLastName,
      grade: studentData.grade,
      dateOfBirth: studentData.dateOfBirth,
      gender: studentData.gender,
      learningDisabilities: studentData.learningDisabilities,
      medicalConditionsType: studentData.medicalConditionsType,
      medicalConditions: studentData.medicalConditions,
      
      parentName: `${studentData.parentFirstName} ${studentData.parentLastName}`,
      parentPhone: studentData.parentPhone,
      parentEmail: studentData.parentEmail,
      relationshipToStudent: studentData.relationshipToStudent,
      authorizedPickup: studentData.authorizedPickup,
      
      programSelection: 'Third-Party Billing',
      billingType: 'third-party',
      needsPickup: false,
      startDate: studentData.startDate,
      
      photoRelease: studentData.photoRelease,
      
      status: 'pending',
      paymentStatus: 'third-party',
      submissionDate: new Date(),
      archived: false,
      assignedDriver: null
    });
    
    return { success: true, id: docRef.id };
  } catch (error) {
    console.error('Error adding third-party student:', error);
    throw new Error('Failed to add third-party student');
  }
};
/**
 * Generate careDates array from camp start and end dates
 */
 function generateCareDates(startDate, endDate) {
  const careDates = [];
  const start = new Date(startDate);
  const end = new Date(endDate);
  
  // Generate dates between start and end
  let currentDate = new Date(start);
  while (currentDate <= end) {
    // Skip weekends for camp
    const dayOfWeek = currentDate.getDay();
    if (dayOfWeek !== 0 && dayOfWeek !== 6) { // 0 = Sunday, 6 = Saturday
      const month = String(currentDate.getMonth() + 1).padStart(2, '0');
      const day = String(currentDate.getDate()).padStart(2, '0');
      const year = String(currentDate.getFullYear()).slice(-2);
      const dateString = `${month}-${day}-${year}`;
      
      careDates.push({
        date: dateString,
        startTime: '09:00 AM',
        endTime: '05:00 PM',
        hours: 8
      });
    }
    
    // Move to next day
    currentDate.setDate(currentDate.getDate() + 1);
  }
  
  return careDates;
}